import { useEffect, useMemo, useState } from "react";
import { History, Loader2, PencilRuler, StickyNote, TrendingUp, Upload, Trash2, Filter } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ICON = {
  "Modifica voci di bilancio": PencilRuler,
  "Eliminazione bilancio": Trash2,
  "Modifica note generali": StickyNote,
  "Modifica commenti report": StickyNote,
  "Modifica piano DSCR": TrendingUp,
  "Caricamento XBRL": Upload,
};

const PERIODS = { all: "Tutto", "7": "Ultimi 7 giorni", "30": "Ultimi 30 giorni", "90": "Ultimi 90 giorni" };

export function ActivityTab({ companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fUser, setFUser] = useState("all");
  const [fAction, setFAction] = useState("all");
  const [fPeriod, setFPeriod] = useState("all");

  useEffect(() => {
    api.get(`/companies/${companyId}/activity`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [companyId]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (e) { return iso; }
  };

  const users = useMemo(() => [...new Set(items.map((a) => a.user_email).filter(Boolean))], [items]);
  const actions = useMemo(() => [...new Set(items.map((a) => a.action).filter(Boolean))], [items]);

  const filtered = useMemo(() => {
    const now = Date.now();
    return items.filter((a) => {
      if (fUser !== "all" && a.user_email !== fUser) return false;
      if (fAction !== "all" && a.action !== fAction) return false;
      if (fPeriod !== "all") {
        const days = Number(fPeriod);
        const t = new Date(a.timestamp).getTime();
        if (isNaN(t) || t < now - days * 86400000) return false;
      }
      return true;
    });
  }, [items, fUser, fAction, fPeriod]);

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...</div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          <h3 className="font-heading font-semibold text-lg tracking-tight">Registro Attività</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={fUser} onValueChange={setFUser}>
            <SelectTrigger className="w-[190px] h-9" data-testid="activity-filter-user"><SelectValue placeholder="Utente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="activity-user-all">Tutti gli utenti</SelectItem>
              {users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fAction} onValueChange={setFAction}>
            <SelectTrigger className="w-[200px] h-9" data-testid="activity-filter-action"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="activity-action-all">Tutti i tipi</SelectItem>
              {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fPeriod} onValueChange={setFPeriod}>
            <SelectTrigger className="w-[150px] h-9" data-testid="activity-filter-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PERIODS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`activity-period-${k}`}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <p className="text-sm text-muted-foreground">Nessuna attività per i filtri selezionati.</p>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <ul className="divide-y divide-border/50">
            {filtered.map((a, i) => {
              const Icon = ICON[a.action] || History;
              return (
                <li key={i} className="flex items-center gap-4 px-5 py-3 hover:bg-accent/20" data-testid={`activity-row-${i}`}>
                  <div className="grid place-items-center h-9 w-9 rounded-lg bg-secondary text-secondary-foreground shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{a.action}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{a.detail}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-mono text-muted-foreground">{fmt(a.timestamp)}</div>
                    <div className="text-[11px] text-muted-foreground truncate max-w-[160px]">{a.user_email}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
